import { FimMode, type DbTarget, type FimPath, type SettingsPatch } from '@bartizo/shared';
import { useEffect, useState, type ReactNode } from 'react';
import { FimPathListEditor } from './FimPathListEditor.js';
import { ALLOWLIST_FIELD, MODE_LABELS, MODULE_SPECS, type FieldSpec } from './settings-fields.js';
import { TargetListEditor } from './TargetListEditor.js';
import { Card } from '../ui/Card.js';
import { SelectField, TextAreaField, TextField } from '../ui/Field.js';

/**
 * One settings layer, as fields (knowledge doc section 11).
 *
 * The shape of the screen follows the shape of a layer: **every field is either
 * overridden here or inherited**, and the checkbox beside it is which. That is not
 * decoration — it is the only way to express "stop overriding this", which a form
 * that just held values could not say at all. Clearing a field would mean setting
 * it to empty; unticking it means letting the layer above decide again.
 *
 * What each field is called and what it is for lives in `settings-fields.ts`. The
 * bounds shown here are for a person; `SettingsPatch` at the API is what refuses a
 * bad value, so nothing depends on this screen being the only way in.
 */

export interface SettingsFormProps {
  settings: SettingsPatch;
  onChange: (next: SettingsPatch) => void;
  /**
   * What applies when a field is not overridden, addressed the same way: the
   * schema's defaults for the global layer, and the server's document for a
   * server's own. Absent where the page cannot know.
   */
  inherited?: Record<string, unknown>;
  disabled?: boolean;
  /** Databases this server's installer prepared, offered as targets on its own layer. */
  detectedDatabases?: readonly DbTarget[] | undefined;
}

type Json = Record<string, unknown>;

const readAt = (settings: SettingsPatch, path: readonly string[]): unknown => {
  let node: unknown = settings;
  for (const key of path) {
    if (typeof node !== 'object' || node === null) return undefined;
    node = (node as Json)[key];
  }
  return node;
};

/**
 * An immutable set, where `undefined` removes.
 *
 * Removing rather than storing a null is the whole point: absence is what "inherit"
 * is, and a layer holding nulls would be a layer overriding everything with nothing.
 * Objects left empty by a removal are pruned for the same reason — `{ modules: {} }`
 * and `{}` should not be two different ways to say the same thing.
 */
function writeAt(settings: SettingsPatch, path: readonly string[], value: unknown): SettingsPatch {
  const [key, ...rest] = path;
  if (key === undefined) return settings;

  const current: Json = settings;
  // Rebuilt without the key rather than deleted from a copy: the same result, and
  // it keeps the object a value rather than something being mutated in place.
  const without: Json = Object.fromEntries(
    Object.entries(current).filter(([name]) => name !== key),
  );

  if (rest.length === 0) {
    return value === undefined ? without : { ...without, [key]: value };
  }

  const child = current[key];
  const updated = writeAt(typeof child === 'object' && child !== null ? child : {}, rest, value);
  return Object.keys(updated).length === 0 ? without : { ...without, [key]: updated };
}

/** What a field holds when it is first overridden, so the control has something. */
const startingValue = (field: FieldSpec, inherited: unknown): unknown => {
  if (inherited !== undefined) return inherited;
  if (field.kind === 'switch') return true;
  if (field.kind === 'number') return field.min;
  if (field.kind === 'choice') return field.options[0]?.value;
  return [];
};

export function SettingsForm({
  settings,
  onChange,
  inherited = {},
  disabled = false,
  detectedDatabases,
}: SettingsFormProps): ReactNode {
  const row = (field: FieldSpec, path: string[]): ReactNode => (
    <SettingRow
      key={path.join('.')}
      field={field}
      path={path}
      value={readAt(settings, path)}
      inherited={inherited[path.join('.')]}
      detected={detectedDatabases}
      disabled={disabled}
      onChange={(value) => {
        onChange(writeAt(settings, path, value));
      }}
    />
  );

  return (
    <>
      <Card
        title="Allowlist"
        description="Addresses that are never banned, whatever CrowdSec decides."
      >
        {row(ALLOWLIST_FIELD, [ALLOWLIST_FIELD.key])}
      </Card>

      {MODULE_SPECS.map((module) => (
        <Card key={module.key} title={module.title} description={module.description}>
          {module.fields.map((field) => row(field, ['modules', module.key, field.key]))}
        </Card>
      ))}
    </>
  );
}

interface RowProps {
  field: FieldSpec;
  path: string[];
  value: unknown;
  inherited: unknown;
  detected?: readonly DbTarget[] | undefined;
  disabled: boolean;
  onChange: (value: unknown) => void;
}

function SettingRow({
  field,
  value,
  inherited,
  detected,
  disabled,
  onChange,
}: RowProps): ReactNode {
  const overridden = value !== undefined;

  return (
    <div className="setting">
      <label className="checkbox">
        <input
          type="checkbox"
          checked={overridden}
          disabled={disabled}
          onChange={(event) => {
            // Unticking removes the field. That is what makes "inherit" reachable
            // from here at all — clearing a value would set it to empty instead.
            onChange(event.target.checked ? startingValue(field, inherited) : undefined);
          }}
        />
        <span className="visually-hidden">Override</span> {field.label}
      </label>

      <Control
        field={field}
        value={value}
        inherited={inherited}
        detected={detected}
        disabled={disabled || !overridden}
        onChange={onChange}
      />
    </div>
  );
}

function Control({
  field,
  value,
  inherited,
  detected,
  disabled,
  onChange,
}: Omit<RowProps, 'path'>): ReactNode {
  const describe = (): string | undefined => {
    const parts: string[] = [];
    if (field.hint !== undefined) parts.push(field.hint);
    if (field.kind === 'number' && field.unit !== undefined) {
      parts.push(
        `In ${field.unit === '%' ? 'per cent' : field.unit === 's' ? 'seconds' : field.unit}.`,
      );
    }
    if (disabled && inherited !== undefined) {
      const shown =
        field.kind === 'choice'
          ? (field.options.find((option) => option.value === inherited)?.label ??
            describeValue(inherited))
          : describeValue(inherited);
      parts.push(`Inherited: ${shown}.`);
    }
    return parts.length > 0 ? parts.join(' ') : undefined;
  };

  if (field.kind === 'switch') {
    return (
      <label className="checkbox">
        <input
          type="checkbox"
          aria-label={`${field.label} value`}
          checked={value === true || (value === undefined && inherited === true)}
          disabled={disabled}
          onChange={(event) => {
            onChange(event.target.checked);
          }}
        />
        <span className="muted text-sm">{describe() ?? 'On'}</span>
      </label>
    );
  }

  if (field.kind === 'number') {
    return (
      <TextField
        label={`${field.label} value`}
        labelHidden
        hint={describe()}
        type="number"
        min={field.min}
        max={field.max}
        step={field.step}
        disabled={disabled}
        value={typeof value === 'number' ? String(value) : ''}
        placeholder={inherited === undefined ? '' : describeValue(inherited)}
        onChange={(event) => {
          const parsed = Number(event.target.value);
          onChange(Number.isFinite(parsed) ? parsed : undefined);
        }}
      />
    );
  }

  if (field.kind === 'choice') {
    const chosen =
      typeof value === 'string' ? value : typeof inherited === 'string' ? inherited : '';
    return (
      <SelectField
        label={`${field.label} value`}
        labelHidden
        hint={describe()}
        disabled={disabled}
        value={chosen}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      >
        {field.options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </SelectField>
    );
  }

  if (field.kind === 'targets') {
    return (
      <>
        {describe() !== undefined && <span className="field-hint">{describe()}</span>}
        <TargetListEditor
          value={Array.isArray(value) ? (value as DbTarget[]) : []}
          detected={detected}
          disabled={disabled}
          onChange={onChange}
        />
      </>
    );
  }

  if (field.kind === 'fim_paths') {
    return (
      <>
        {describe() !== undefined && <span className="field-hint">{describe()}</span>}
        <FimPathListEditor
          value={Array.isArray(value) ? (value as FimPath[]) : []}
          disabled={disabled}
          onChange={onChange}
        />
      </>
    );
  }

  if (field.kind === 'numbers') {
    return (
      <StringListControl
        label={field.label}
        hint={describe()}
        placeholder={field.placeholder}
        disabled={disabled}
        value={Array.isArray(value) ? value.map(String) : []}
        onChange={(lines) => {
          // A line that is not a number is sent as it is, so the API refuses it
          // with a reason rather than this screen quietly dropping it.
          onChange(lines.map((line) => (/^\d{1,9}$/.test(line) ? Number(line) : line)));
        }}
      />
    );
  }

  // Only `strings` is left: the five above returned.
  return (
    <StringListControl
      label={field.label}
      hint={describe()}
      placeholder={field.placeholder}
      disabled={disabled}
      value={Array.isArray(value) ? (value as string[]) : []}
      onChange={onChange}
    />
  );
}

/**
 * A list, one entry per line.
 *
 * The draft is held here rather than derived from the value on every keystroke,
 * because the two disagree while somebody is typing: a newline they just pressed is
 * an empty entry the parse would drop, and the cursor would jump back a line.
 */
function StringListControl({
  label,
  hint,
  placeholder,
  disabled,
  value,
  onChange,
}: {
  label: string;
  hint: string | undefined;
  placeholder: string | undefined;
  disabled: boolean;
  value: string[];
  onChange: (value: string[]) => void;
}): ReactNode {
  const [draft, setDraft] = useState(() => value.join('\n'));

  useEffect(() => {
    // Re-seeded when the value changes underneath — a reload, or a revert — but
    // not while the draft still says the same thing.
    const joined = value.join('\n');
    setDraft((current) => (parseLines(current).join('\n') === joined ? current : joined));
  }, [value]);

  return (
    <TextAreaField
      label={`${label} list`}
      labelHidden
      hint={hint}
      rows={3}
      spellCheck={false}
      disabled={disabled}
      placeholder={placeholder}
      value={draft}
      onChange={(event) => {
        setDraft(event.target.value);
        onChange(parseLines(event.target.value));
      }}
    />
  );
}

const parseLines = (text: string): string[] =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

const describeItem = (item: unknown): string => {
  if (typeof item !== 'object' || item === null) return String(item);
  if ('name' in item) return String(item.name);
  if ('path' in item && 'mode' in item) {
    const mode = FimMode.safeParse(item.mode);
    return `${String(item.path)} (${mode.success ? MODE_LABELS[mode.data].toLowerCase() : String(item.mode)})`;
  }
  return JSON.stringify(item);
};

const describeValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    if (value.length === 0) return 'nothing';
    // A database target is named by its name, a watched path by itself and its
    // mode, anything else by itself.
    return value.map(describeItem).join(', ');
  }
  if (typeof value === 'boolean') return value ? 'on' : 'off';
  return String(value);
};
